import { presignedUrl } from '@/modules/Upload/services'
import { ErrorResponse } from '@/types/shared'
import { sanitizeFileName } from '@/utils/function'
import axios, { AxiosError } from 'axios'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TicketAttachment } from './response'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface UseUploadFileProps {
    prefix: string
    isPublic?: boolean
    onSuccess?: (
        data: PromiseSettledResult<Partial<TicketAttachment>>[]
    ) => void
    onError?: (err: AxiosError<ErrorResponse>) => void
}
const useUploadFile = ({
    onSuccess,
    onError,
    prefix,
    isPublic = true,
}: UseUploadFileProps) => {
    const { t } = useTranslation()
    const [percent, setPercent] = useState<Record<string, number>>({})

    const uploadFile = async (
        file: File
    ): Promise<Partial<TicketAttachment>> => {
        const fileName = `${Date.now()}-${sanitizeFileName(file.name)}`
        const response = await presignedUrl({
            isPublic,
            prefix,
            key: fileName,
        })

        if (!response?.result?.url) {
            throw new Error(t(`upload-file-failed`))
        }

        await axios
            .put(response?.result?.url, file, {
                onUploadProgress: (p) => {
                    const { loaded, total } = p
                    const percent = Math.floor((loaded * 100) / (total || 1))
                    setPercent((prev) => ({
                        ...prev,
                        [file.name]: percent,
                    }))
                },
                headers: {
                    'Content-Type': file.type,
                },
            })
            .catch((err) => {
                console.log(err)
                throw new Error(t(`upload-file-failed`))
            })

        return {
            ticket_attachment_path: [
                `${prefix}`,
                `${encodeURIComponent(fileName)}`,
            ]
                .join('/')
                .replace('//', '/'),
            ticket_attachment_size: file.size,
            ticket_attactment_name: file.name,
            ticket_attachment_type: file.type,
        }
    }

    const uploadFiles = async (files: File[]) => {
        return Promise.allSettled(
            files.map(async (file) => await uploadFile(file))
        )
    }

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: uploadFiles,
        },
        {
            reactQueryKeys: QUERY_KEYS.TICKETS,
            onSuccess: (data) => {
                onSuccess?.(data)
            },
            onError: (err: AxiosError<ErrorResponse>) => {
                onError?.(err)
            },
        }
    )

    return {
        uploadAsync: mutateAsync,
        isUploading: isPending,
        percent,
    }
}

export default useUploadFile
